import { Component, OnInit, ViewChild } from '@angular/core';
import { AnnouncementInterface } from '@careapp/angular-sdk';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Rx';

import * as actions from '../../store/actions/index';
import {
  RequestMemoStatisticsAction,
  RequestMembersStatisticsAction,
  RequestCustomersStatisticsAction
} from '../../store/actions/statistics.actions';
import * as selectors from '../../store/reducers/index';
import { getPopularCategories } from '../../store/selectors/statistics.selectors';
import { environment } from '../../../environments/environment';
import {
  popularMemoTypesTable,
  activeMembersTable,
  activeCustomersTable,
  getMemoCount,
  checkInScoreTally,
  checkInTalliesForDateRange,
  checkInCount
} from '../../store/selectors/dashboard.selectors';
import { checkInsWithLocation } from '../../store/selectors/check-ins.selectors';
import { getEntities } from '../../store/reducers';
import { CUSTOMERS } from '../../store/reducers/index';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { feedbackWithMember } from '../../store/selectors/feedback.selectors';
import { ORGANISATION_ADMIN_ROLE, ADMIN_ROLE } from '@careapp/core';

@Component({
  selector: 'ca-dashboard-component',
  templateUrl: 'dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  adminRoles = [ORGANISATION_ADMIN_ROLE, ADMIN_ROLE];
  activeUserLabels = [
    { label: 'User' },
    { icon: 'message', tooltip: 'Number of messages sent' },
    { icon: 'location_on', tooltip: 'Number of check ins' }
  ];
  activeCustomerLabels = [
    { label: 'Community' },
    { icon: 'message', tooltip: 'Number of messages sent' },
    { icon: 'location_on', tooltip: 'Number of check ins' },
    { icon: 'group', tooltip: 'Community size' }
  ];
  popularCategoriesLabels = [{ label: 'Category' }, { icon: 'message' }];
  popularTypesLabels = [{ label: 'Type' }, { icon: 'message' }];

  public BADGES = [
    '/assets/images/badges/poor.png',
    '/assets/images/badges/fair.png',
    '/assets/images/badges/good.png',
    '/assets/images/badges/excellent.png'
  ];

  clusterStyles = [
    {
      url: '/assets/images/clusters/m1.png',
      backggroundPosition: '1px, 0px',
      textColor: 'white',
      height: 52,
      width: 52
    },
    {
      url: '/assets/images/clusters/m2.png',
      textColor: 'white',
      height: 52,
      width: 52
    },
    {
      url: '/assets/images/clusters/m3.png',
      textColor: 'white',
      height: 52,
      width: 52
    },
    {
      url: '/assets/images/clusters/m4.png',
      textColor: 'white',
      height: 52,
      width: 52
    },
    {
      url: '/assets/images/clusters/m5.png',
      textColor: 'white',
      height: 52,
      width: 52
    }
  ];

  data: Observable<any>;
  loading$: Observable<boolean>;
  organisation$: Observable<any>;
  announcements$: Observable<AnnouncementInterface[]>;
  popularCategories$: Observable<Array<{ id: string; name: string; icon: string; score: number }>>;
  popularTypes$: Observable<Array<{ name: string; score: number }>>;
  memoCount$: Observable<number>;
  mappableCheckIns$: Observable<Array<any>>;
  checkInCount$: Observable<number>;
  customers$: Observable<any>;
  feedback$: Observable<Array<any>>;
  activeTab$: Observable<number>;

  checkInCounts$: Observable<{ poor: number; fair: number; good: number; excellent: number }>;
  checkInsByDate$: Observable<{
    [date: string]: { excellent: number; good: number; fair: number; poor: number };
  }>;

  activeMembers$: Observable<
    Array<{
      avatarUrl: string;
      name: string;
      scores: Array<number>;
    }>
  >;
  activeCustomers$: Observable<
    Array<{
      avatarUrl: string;
      name: string;
      scores: Array<number>;
    }>
  >;
  checkins$: Observable<
    Array<{
      latitude: number;
      longitude: number;
      relatesToId: string;
      iconUrl: string;
      label: string;
    }>
  >;

  constructor(private store: Store<any>, private route: ActivatedRoute, private router: Router) {
    this.loading$ = this.store.select(selectors.statistics.isMemoStatsLoading);
    this.organisation$ = this.store.select(selectors.getSelected(selectors.ORGANISATIONS));
    this.announcements$ = this.store.select(selectors.getAll(selectors.ANNOUNCEMENTS));

    this.popularCategories$ = this.store.select(getPopularCategories);
    this.popularTypes$ = this.store.select(popularMemoTypesTable);
    this.activeMembers$ = this.store.select(activeMembersTable);
    this.activeCustomers$ = this.store.select(activeCustomersTable);
    this.memoCount$ = this.store.select(getMemoCount);
    this.mappableCheckIns$ = this.store.select(checkInsWithLocation);
    this.customers$ = this.store.select(getEntities(CUSTOMERS));
    this.checkInCounts$ = this.store.select(checkInScoreTally);
    this.checkInsByDate$ = this.store.select(checkInTalliesForDateRange);
    this.checkInCount$ = this.store.select(checkInCount);
    this.feedback$ = this.store.select(feedbackWithMember);

    this.activeTab$ = this.route.queryParams.map(params => {
      if (params.tab) {
        switch (params.tab) {
          case 'stats':
            return 0;
          case 'feedback':
            return 1;
          case 'notices':
            return 2;
        }
      }
      return 0;
    });
  }
  ngOnInit() {
    this.store.dispatch(new RequestMemoStatisticsAction());
    this.store.dispatch(
      new RequestMembersStatisticsAction({ limit: environment.STATS_TABLE_LIMIT })
    );
    this.store.dispatch(
      new RequestCustomersStatisticsAction({ limit: environment.STATS_TABLE_LIMIT })
    );
    this.store.dispatch(actions.MemoCategories.RequestAllAction);
    this.store.dispatch(actions.Announcements.RequestAllAction);
    this.store.dispatch(actions.CheckIns.RequestAllAction);
    this.store.dispatch(actions.Feedback.RequestAllAction);
  }

  tabChange(index: number) {
    let tabName;
    switch (index) {
      case 0:
        tabName = 'stats';
        break;
      case 1:
        tabName = 'feedback';
        break;
      case 2:
        tabName = 'notices';
        break;
    }
    this.router.navigate([], { queryParams: { tab: tabName } });
  }
}
